import { ChevronDownIcon } from "@chakra-ui/icons";
import {
  Button,
  Center,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useState } from "react";
import { BiSelectMultiple } from "react-icons/bi";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocation } from "wouter";

import { AuthMode, Scenario } from "@nens/ldo/api-v1";
import { BulkAction, useExcelDownloadBulkAction } from "@nens/ldo/hooks";
import { combineUrlAndParams } from "@nens/shared-utils";

import { useAddToExportBulkAction } from "../../features/bulk-exports/add-to-export-bulk-action";
import { AddToExportModal } from "../add-to-export-modal";

interface BulkActionsMenuProps {
  authMode: AuthMode;
  scenarioIds: string[];
  getAllScenariosInFilter: () => Promise<Scenario[]>;
}

export function BulkActionsMenu({
  authMode,
  scenarioIds,
  getAllScenariosInFilter,
}: BulkActionsMenuProps) {
  const intl = useIntl();
  const toast = useToast();

  const setLocation = useLocation()[1];
  const { isOpen: isOpenSome, onOpen: onOpenSome, onClose: onCloseSome } = useDisclosure();
  const { isOpen: isOpenAll, onOpen: onOpenAll, onClose: onCloseAll } = useDisclosure();
  const [fetchingScenarios, setFetchingScenarios] = useState(false);
  const [allScenarioIds, setAllScenarioIds] = useState<string[]>([]);

  const excelDownloadBulkAction = useExcelDownloadBulkAction({
    type: "scenarios",
    scenarioIds,
    mode: authMode,
  });
  const excelDownloadAllBulkAction = useExcelDownloadBulkAction({
    type: "all",
    mode: authMode,
  });
  const { addToExportBulkAction, bulkExports } = useAddToExportBulkAction({
    title: intl.formatMessage({
      id: "bulkaction.export.add_selected_scenario_to_export",
      defaultMessage: "Add selected scenarios to existing export",
    }),
    disabled: scenarioIds.length === 0,
    onClick: onOpenSome,
  });

  const openAddAllScenariosToExport = async () => {
    setFetchingScenarios(true);
    const allScenarios = await getAllScenariosInFilter();
    setFetchingScenarios(false);
    if (allScenarios.some((scenario) => !scenario.canBeExported)) {
      toast({
        title: intl.formatMessage({
          id: "bulkaction.export.cannot_export",
          defaultMessage: "Cannot export all scenarios",
        }),
        description: intl.formatMessage({
          id: "bulkaction.export.cannot_export_description",
          defaultMessage:
            "Only scenarios with a status of import checked, quality control requested, quality control rejected or checked can be exported.",
        }),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } else {
      setAllScenarioIds(allScenarios.map((scenario) => scenario.id.toString()));
      onOpenAll();
    }
  };

  const { addToExportBulkAction: addToExportBulkActionAll, bulkExports: bulkExportsAll } =
    useAddToExportBulkAction({
      title: intl.formatMessage({
        id: "bulkaction.export.add_all_scenario_to_export",
        defaultMessage: "Add all scenarios in filter to existing export",
      }),
      onClick: openAddAllScenariosToExport,
    });

  const compareScenariosBulkAction: BulkAction = {
    title: intl.formatMessage({
      id: "bulkaction.compare_scenarios",
      defaultMessage: "Compare scenarios",
    }),
    hidden: false,
    disabled: scenarioIds.length === 0,
    running: false,
    run: () =>
      setLocation(combineUrlAndParams("/scenarios/compare", { ids: scenarioIds.join(",") })),
  };

  const bulkActions: BulkAction[] = [
    excelDownloadBulkAction,
    excelDownloadAllBulkAction,
    addToExportBulkAction,
    { ...addToExportBulkActionAll, running: fetchingScenarios },
    compareScenariosBulkAction,
  ].filter((action) => !action.hidden);

  if (bulkActions.length === 0) {
    return null;
  }

  const showSpinner = bulkActions.some((action) => action.running);

  return (
    <Center>
      <Menu>
        <MenuButton
          size="sm"
          as={Button}
          leftIcon={<BiSelectMultiple />}
          rightIcon={<ChevronDownIcon />}
          borderColor="bij12_1.400"
          color="bij12_1.700"
          variant="outline"
          bgColor="white"
        >
          <FormattedMessage
            id="listpage.bulk_action_button"
            defaultMessage="Select a bulk action"
          />
        </MenuButton>
        <MenuList color="bij12_1.700" bgColor="white">
          {bulkActions.map((bulkAction) => (
            <MenuItem
              key={bulkAction.title}
              isDisabled={bulkAction.disabled}
              fontWeight="normal"
              onClick={bulkAction.run}
            >
              {bulkAction.title}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
      {showSpinner ? <Spinner /> : null}
      <AddToExportModal
        scenarioIds={scenarioIds}
        onClose={onCloseSome}
        isOpen={isOpenSome}
        bulkExports={bulkExports}
      />
      <AddToExportModal
        scenarioIds={allScenarioIds}
        onClose={onCloseAll}
        isOpen={isOpenAll}
        bulkExports={bulkExportsAll}
      />
    </Center>
  );
}
